/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { getConfig } from './config.js';
import {
    externalLinkInNewTab,
    internalLinkTarget,
    isExternalLink,
    rewriteInternalUrlsForApp,
} from './links.js';
import {
    linksToMediaPlayer,
    parseMediaURL,
    rewriteMediaURL,
    rewriteMediaUrls,
} from './media.js';

/*
 * Blocking third-party media is a consent decision, and restoring it is
 * what granting consent means — so these preprocessors need to ask the
 * consent module, and the consent module needs to call them back.
 *
 * Importing each other for that was a cycle, and a cycle with a top-level
 * side effect on one side is not merely untidy: whichever module the
 * bundler evaluates second reads the other's bindings before they are
 * initialised, and the page dies on a `ReferenceError`. The consent module
 * hands its policy in instead, so the dependency runs one way.
 */

/** The consent policy, supplied by the cookie module. */
let consent = null;

/**
 * Tell the preprocessors which consent policy governs external media.
 *
 * @param {object|null} policy - An object answering `enabled`,
 *   `hasConsented(category)`, `_readSavedConsent()` and `showSettings()`.
 */
export function setConsentPolicy(policy) {
    consent = policy ?? null;
}

let privacyDelegationBound = false;

/**
 * Content preprocessors:
 *
 * Register DOM transforms that must run *before* content is shown/attached.
 * Includes a default that blocks external media by moving fetching attributes
 * (src/srcset/poster/href) into data-* so they can be restored later.
 */
export const contentPreprocessors = (() => {
        // Helper: move/restore fetch attributes (src/srcset/poster/href)
        const ATTR_PREFIX = 'data-blocked-';
        // Optional link sanitizer hook used by some renderers (e.g. popups)
        let _linkSanitizer = null;

        function moveFetchAttrs(el, FETCH_ATTRS) {
            FETCH_ATTRS.forEach((attr) => {
                if (el.hasAttribute(attr)) {
                    el.setAttribute(ATTR_PREFIX + attr, el.getAttribute(attr));
                    el.removeAttribute(attr);
                }
            });
        }

        function restoreFetchAttrs(el, FETCH_ATTRS) {
            FETCH_ATTRS.forEach((attr) => {
                const name = ATTR_PREFIX + attr;
                if (el.hasAttribute(name)) {
                    el.setAttribute(attr, el.getAttribute(name));
                    el.removeAttribute(name);
                }
            });
        }

        const _fns = [];
        const FETCH_ATTRS = ['href', 'poster', 'src', 'srcset'];
        const NODE_SELECTOR = [
            'audio',
            'iframe',
            'img',
            'link[rel="preload"]',
            'link[rel="prefetch"]',
            'link[rel="stylesheet"]',
            'picture source',
            'source',
            'track',
            'script[src]',
            'video'
        ].join(',');

        // Use the exact placeholder pattern used today in cookie.js:
        // <div.external-content-placeholder role="note" aria-label=msg>msg<button.privacy-settings-button>…</button></div>
        function createPlaceholder(message, changeSettingsLabel) {
            const placeholder = document.createElement('div');
            placeholder.className = 'external-content-placeholder';
            placeholder.setAttribute('role', 'note');
            if (message) {
                placeholder.setAttribute('aria-label', message);
                placeholder.textContent = message;
            }
            const btn = document.createElement('button');
            btn.className = 'privacy-settings-button';
            btn.textContent = changeSettingsLabel || '';
            placeholder.appendChild(btn);
            return placeholder;
        }

        function closestPolicy(el) {
            const container = el.closest?.('[data-cc-external]');
            const mode =
                container &&
                String(
                    container.getAttribute('data-cc-external') || ''
                ).toLowerCase();
            return mode === 'hide' || mode === 'placeholder'
                ? mode
                : 'placeholder';
        }

        function markHidden(el) {
            el.setAttribute('data-cc-blocked', 'hide');
            // Use attribute + style to be robust
            el.setAttribute('hidden', 'hidden');
            try {
                el.style.display = 'none';
            } catch {
                // A read-only style declaration; the `hidden` attribute
                // above already did the work.
            }
        }

        function unmarkHidden(el) {
            el.removeAttribute('data-cc-blocked');
            el.removeAttribute('hidden');
            try {
                el.style.display = '';
            } catch {
                // A read-only style declaration; removing the `hidden`
                // attribute above is what actually reveals it.
            }
        }

        // Block external media per container policy (hide vs placeholder)

        function runBlocker(root, { blockedMsg, changeSettingsLabel } = {}) {
            const ctx = (root);
            if (!ctx) return;

            // Respect cookie consent:
            //  - if the system is disabled → do nothing
            //  - if the user has consented → do nothing
            //  - if not initialized yet, but a saved cookie allows it → do nothing
            if (
                consent &&
                consent.enabled === false
            ) {
                return;
            }
            if (
                typeof consent?.hasConsented === 'function' &&
                consent.hasConsented('external')
            ) {
                return;
            }
            if (
                consent &&
                !consent.initialized &&
                typeof consent._readSavedConsent === 'function'
            ) {
                const saved = consent._readSavedConsent();
                if (saved && saved.external === true) return;
            }

            ctx.querySelectorAll(NODE_SELECTOR).forEach((el) => {
                // pick the element to replace (handle <source> and <picture>)
                let target = el;
                if (
                    el.tagName?.toLowerCase() === 'source' &&
                    el.parentElement &&
                    /^(video|audio)$/i.test(el.parentElement.tagName)
                ) {
                    target = el.parentElement;
                }
                const pictureAncestor = target.closest?.('picture');
                if (pictureAncestor) target = pictureAncestor;

                // Already hidden? (skip)
                if (target.hasAttribute('data-cc-blocked')) return;

                // any fetch attribute external?
                let hasExternal = false;
                // Check the element being iterated (not only target), because NODE_SELECTOR iterates children too
                for (const attr of FETCH_ATTRS) {
                    if (
                        el.hasAttribute(attr) &&
                        isExternalLink(el.getAttribute(attr))
                    ) {
                        hasExternal = true;
                        break;
                    }
                }
                if (!hasExternal) return;

                // Move fetch attrs off the *whole subtree* so nothing loads accidentally.
                target
                    .querySelectorAll('*')
                    .forEach((n) => moveFetchAttrs(n, FETCH_ATTRS));
                moveFetchAttrs(target, FETCH_ATTRS);

                const policy = closestPolicy(target);
                if (policy === 'hide') {
                    markHidden(target);
                } else {
                    // placeholder: keep original (already attribute-blocked) in <template>, show standard placeholder
                    const placeholder = createPlaceholder(
                        blockedMsg,
                        changeSettingsLabel
                    );
                    const tpl = document.createElement('template');
                    tpl.setAttribute('data-original', '1');
                    tpl.content.appendChild(target.cloneNode(true));
                    placeholder.appendChild(tpl);
                    target.replaceWith(placeholder);
                }
            });
        }

        const api = {
            register(fn) {
                if (typeof fn === 'function') _fns.push(fn);
            },
            /**
             * Set a temporary sanitizer for internal links processed by the
             * common preprocessor. Returns a function to restore the previous sanitizer.
             * @param {(this: HTMLAnchorElement) => void | undefined} fn
             * @returns {() => void}
             */
            setLinkSanitizer(fn) {
                const prev = _linkSanitizer;
                _linkSanitizer = typeof fn === 'function' ? fn : null;
                return () => {
                    _linkSanitizer = prev;
                };
            },
            applyToElement(container, options = {}) {
                const root = (container);
                _fns.forEach((fn) => {
                    try {
                        fn(root, options);
                    } catch (err) {
                        console.error('Content preprocessor failed:', err);
                    }
                });
            },
            applyToHTML(html, options = {}) {
                if (!html) return html;
                const wrapper = document.createElement('div');
                wrapper.innerHTML = html;
                this.applyToElement(wrapper, options);
                return wrapper.innerHTML;
            },
            /**
             * Restore previously blocked external media:
             *  - placeholders: swap back original subtree and restore fetch attrs
             *  - hidden nodes: restore fetch attrs + unhide
             */
            restoreBlockedExternal(root = document) {
                const ctx = (root) || document;
                // Placeholders → originals
                ctx.querySelectorAll(
                    '.external-content-placeholder template[data-original]'
                ).forEach((tpl) => {
                    const placeholder = tpl.parentElement;
                    const original = tpl.content?.firstElementChild
                        ? tpl.content.firstElementChild.cloneNode(true)
                        : null;
                    if (!placeholder || !original) return;
                    // restore attributes in entire subtree
                    original
                        .querySelectorAll('*')
                        .forEach((n) => restoreFetchAttrs(n, FETCH_ATTRS));
                    restoreFetchAttrs(original, FETCH_ATTRS);
                    placeholder.replaceWith(original);
                });
                // Hidden nodes (map area)
                ctx.querySelectorAll('[data-cc-blocked="hide"]').forEach(
                    (node) => {
                        node.querySelectorAll('*').forEach((n) =>
                            restoreFetchAttrs(n, FETCH_ATTRS)
                        );
                        restoreFetchAttrs(node, FETCH_ATTRS);
                        unmarkHidden(node);
                    }
                );
            },
            defaults: {
                rewriteInternalUrlsForApp: (root) => {
                    root.innerHTML = rewriteInternalUrlsForApp(
                        root.innerHTML
                    );
                },
                /**
                 * Common content processing that used to live in Popup code:
                 *  - rewrite media URLs (img/audio/video/source/poster)
                 *  - link tweaks (external => new tab, _target param support)
                 *  - optional link sanitizer hook
                 *  - convert media links to players
                 */
                processCommonContent: (root) => {
                    try {
                        rewriteMediaUrls(root, { useLegacy: true });
                    } catch (err) {
                        console.error('rewriteMediaUrls failed:', err);
                    }
                    try {
                        root.querySelectorAll(':link').forEach((link) => {
                            try {
                                externalLinkInNewTab.call(link);
                                internalLinkTarget.call(link);
                                if (_linkSanitizer) _linkSanitizer.call(link);
                            } catch (err) {
                                console.error(
                                    'Link preprocessing failed:',
                                    err
                                );
                            }
                        });
                    } catch (err) {
                        console.error('Querying links failed:', err);
                    }
                    try {
                        linksToMediaPlayer(root, {
                            useLegacy: true
                        });
                    } catch (err) {
                        console.error('linksToMediaPlayer failed:', err);
                    }
                },
                /**
                 * Rewrite <img> elements whose src is a media API URL into
                 * <picture> elements with AVIF and WebP <source> alternatives
                 * and three size variants (xsmall/small/default/large) in srcset.
                 * PNG, GIF, and SVG images are left unchanged (single format).
                 */
                imgToPicture: (root) => {
                    const SIZES =
                        '(max-width: 320px) 320px, (max-width: 640px) 640px, (max-width: 1280px) 1280px, 2560px';
                    const FORMATS = [
                        { suffix: '.avif', type: 'image/avif' },
                        { suffix: '.webp', type: 'image/webp' }
                    ];
                    const VARIANTS = [
                        { name: 'xsmall', width: 320 },
                        { name: 'small', width: 640 },
                        { name: 'default', width: 1280 },
                        { name: 'large', width: 2560 }
                    ];
                    const SKIP_SUFFIXES = new Set(['.png', '.gif', '.svg']);

                    root.querySelectorAll('img').forEach((img) => {
                        if (
                            img.parentElement?.tagName?.toLowerCase() ===
                            'picture'
                        ) {
                            return;
                        }

                        const src = img.src || img.getAttribute('src') || '';
                        if (!src || isExternalLink(src)) return;

                        const parsed = parseMediaURL(src);
                        if (!parsed.clientId) return;

                        // Use variantSuffix when present (URL has a variant
                        // path segment like "default/icon.svg").  Fall back to
                        // the extension of parsed.filename for URLs that have
                        // no variant path segment (e.g. "/media/icon.svg"),
                        // where parseMediaURL returns filename="icon.svg" and
                        // variantSuffix=undefined.
                        const effectiveSuffix = (
                            parsed.variantSuffix ||
                            `.${(parsed.filename || '').split('.').pop()}`
                        ).toLowerCase();
                        if (SKIP_SUFFIXES.has(effectiveSuffix)) return;

                        const makeUrl = (variant, variantSuffix) =>
                            rewriteMediaURL(src, {
                                variant,
                                variantSuffix,
                                useLegacy: true
                            });

                        const picture = document.createElement('picture');
                        for (const { suffix: fmtSuffix, type } of FORMATS) {
                            const source = document.createElement('source');
                            source.type = type;
                            source.setAttribute(
                                'srcset',
                                VARIANTS.map(
                                    ({ name, width }) =>
                                        `${makeUrl(name, fmtSuffix)} ${width}w`
                                ).join(', ')
                            );
                            source.setAttribute('sizes', SIZES);
                            picture.appendChild(source);
                        }

                        img.src = makeUrl('default', '.jpg');
                        const parent = img.parentNode;
                        if (!parent) return;
                        parent.insertBefore(picture, img);
                        picture.appendChild(img);
                    });
                },
                // Keep the name for backwards compatibility; now applies per-container policy.
                blockExternalMediaWithPlaceholders: (root) => {
                    const ui = getConfig()?.uiText || {};
                    runBlocker(root, {
                        blockedMsg: ui?.blockedMsg || '',
                        changeSettingsLabel: ui?.changePrivacySettings || ''
                    });
                },
                /*
                 * Re-apply blocking based on each container’s data-cc-external mode.
                 * Useful when consent is revoked.
                 */
                reapplyPolicy(root = document) {
                    const ctx = (root) || document;
                    const ui = getConfig()?.uiText || {};
                    // Only apply inside explicit containers to avoid blocking global assets
                    const containers =
                        ctx.querySelectorAll('[data-cc-external]');
                    containers.forEach((container) =>
                        runBlocker(container, {
                            blockedMsg: ui?.blockedMsg || '',
                            changeSettingsLabel: ui?.changePrivacySettings || ''
                        })
                    );
                }
            }
        };

        return api;
    })();

// Register defaults (order matters):
contentPreprocessors.register(
    contentPreprocessors.defaults.rewriteInternalUrlsForApp
);
contentPreprocessors.register(
    contentPreprocessors.defaults.processCommonContent
);
contentPreprocessors.register(
    contentPreprocessors.defaults.imgToPicture
);
contentPreprocessors.register(
    contentPreprocessors.defaults.blockExternalMediaWithPlaceholders
);

/**
 * Ensure privacy settings button works even when placeholders were created
 * through applyToHTML (event listeners are lost during HTML serialization).
 * We use a single delegated handler on document.
 */
(function bindPrivacySettingsDelegationOnce() {
    if (privacyDelegationBound) return;
    document.addEventListener(
        'click',
        (ev) => {
            var target = ev.target;
            // IE11-safe closest check (polyfilled on map.html)
            var btn =
                target &&
                (target.closest
                    ? target.closest(
                          '.external-content-placeholder .privacy-settings-button'
                      )
                    : null);
            if (btn) {
                ev.preventDefault();
                try {
                    consent.showSettings();
                } catch (err) {
                    console.error('Opening privacy settings failed:', err);
                }
            }
        },
        true
    );
    privacyDelegationBound = true;
})();
